import React, { useState } from 'react'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material'
import { Email, ArrowBack } from '@mui/icons-material'
import { authService } from '../../services/api'
import { ForgotPasswordRequest } from '../../types/api'
import AuthPageWrapper from './AuthPageWrapper'

interface ForgotPasswordFormProps {
  onBackToLogin: () => void
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState<string>('')
  const [emailError, setEmailError] = useState<string>('')

  const validateEmail = (): boolean => {
    if (!email) {
      setEmailError('Email-ul este obligatoriu')
      return false
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Email-ul nu este valid')
      return false
    }
    setEmailError('')
    return true
  }

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    if (emailError) {
      setEmailError('')
    }
    if (error) {
      setError('')
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    
    if (!validateEmail()) {
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const request: ForgotPasswordRequest = { email }
      await authService.forgotPassword(request)
      setIsSubmitted(true)
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Eroare la trimiterea email-ului'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  if (isSubmitted) {
    return (
      <AuthPageWrapper>
        <Paper
          elevation={0}
          sx={{
            padding: 3,
            width: '100%',
            maxWidth: '380px',
            borderRadius: 8,
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: 'none',
            
            transition: 'border-color 0.3s ease',
            '&:hover': {
              border: '1px solid rgba(255, 255, 255, 0.2)',
            },
            
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
          }}
        >
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              backgroundColor: 'rgba(76, 175, 80, 0.15)',
              color: '#4CAF50',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              '& .MuiAlert-icon': {
                color: '#4CAF50'
              }
            }}
          >
            Email trimis cu succes!
          </Alert>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#FFFFFF',
              fontSize: '20px',
              fontWeight: 500,
              mb: 2
            }}
          >
            Verificați email-ul
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px',
              mb: 2
            }}
          >
            Am trimis instrucțiuni de resetare a parolei la{' '}
            <strong style={{ color: '#FFFFFF' }}>{email}</strong>
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '13px',
              mb: 3
            }}
          >
            Verificați inbox-ul și folderul spam
          </Typography>
          
          <Button
            variant="contained"
            onClick={onBackToLogin}
            startIcon={<ArrowBack />}
            fullWidth
            sx={{
              py: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#FFFFFF',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '16px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            Înapoi la autentificare
          </Button>
        </Paper>
      </AuthPageWrapper>
    )
  }

  return (
    <AuthPageWrapper>
      <Paper
        elevation={0}
        sx={{
          padding: 3,
          width: '100%',
          maxWidth: '320px',
          borderRadius: 8,
          background: 'transparent',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'none',
          
          transition: 'border-color 0.3s ease',
          '&:hover': {
            border: '1px solid rgba(255, 255, 255, 0.2)',
          },
          
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              color: '#FFFFFF',
              fontSize: '24px',
              fontWeight: 500,
              marginBottom: 1,
              letterSpacing: '0.5px'
            }}
          >
            Ați uitat parola?
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px'
            }}
          >
            Introduceți email-ul pentru resetare
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              error={!!emailError}
              helperText={emailError}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                  </InputAdornment>
                )
              }}
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  paddingLeft: '50px',
                  '& input': {
                    '&:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                      WebkitTextFillColor: '#FFFFFF !important',
                      backgroundColor: '#000000 !important',
                      boxShadow: '0 0 0 1000px #000000 inset !important',
                      transition: 'background-color 5000s ease-in-out 0s !important',
                    },
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mt: 3, mb: 2 }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                py: 1.5,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: '#FFFFFF',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '16px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                },
                '&:disabled': {
                  opacity: 0.5,
                  color: '#FFFFFF',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Trimite link resetare'
              )}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={onBackToLogin}
              disabled={isLoading}
              sx={{ 
                cursor: 'pointer',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                '&:hover': {
                  color: '#FFFFFF',
                }
              }}
            >
              Înapoi la autentificare
            </Link>
          </Box>
        </Box>
      </Paper>
    </AuthPageWrapper>
  )
}

export default ForgotPasswordForm
